import _ from "lodash";
import { graphlib } from "dagre-d3";
import { TaskType } from "../../utils/constants";

export default class WorkflowDAG {
  constructor(execution, workflowDef, view) {
    this.execution = execution;
    this.workflowDef = workflowDef;
    this.view = view;

    this.graph = new graphlib.Graph({ directed: true, compound: false });
    this.taskResultsByRef = new Map();
    this.taskResultsById = new Map();

    this.constructGraph();
  }

  addTaskResult(ref, task) {
    if (!this.taskResultsByRef.has(ref)) {
      this.taskResultsByRef.set(ref, []);
    }
    this.taskResultsByRef.get(ref).push(task);
    this.taskResultsById.set(task.taskId, task);
  }

  getLastTaskResult(ref) {
    if (this.taskResultsByRef.has(ref)) {
      return _.last(this.taskResultsByRef.get(ref));
    } else {
      return null;
    }
  }

  constructGraph() {
    const { workflowDef, execution } = this;

    // Definition Only
    if (workflowDef) {
      this.defToGraph(workflowDef);
    }
    // Definition part of Execution object
    else if (execution) {
      let isTerminated = false;
      for (let task of execution.tasks) {
        if (task["taskType"] === "TERMINATE") isTerminated = true;

        this.addTaskResult(task["referenceTaskName"], task);
      }

      if (execution.status) {
        this.addTaskResult("__start", { status: "COMPLETED" });
        if (execution.status === "COMPLETED" && !isTerminated) {
          this.addTaskResult("__final", { status: "COMPLETED" });
        }
      }

      this.defToGraph(execution.workflowDefinition);
    } else {
      throw new Error(
        "Must pass either workflowDef or execution in constructor"
      );
    }
  }

  defToGraph(workflowDef) {
    var tasks = workflowDef.tasks ? workflowDef.tasks : [];
    const definedTasks = [...tasks];
    definedTasks.unshift({
      type: "TERMINAL",
      name: "start",
      taskReferenceName: "__start",
    });

    definedTasks.push({
      type: "TERMINAL",
      name: "final",
      taskReferenceName: "__final",
    });

    // Recursively process tasks
    this.processTaskList(definedTasks, []);

    // All branches are terminated by a user-defined 'TERMINATE' task.
    if (_.isEmpty(this.graph.inEdges("__final"))) {
      this.graph.removeNode("__final");
    }
  }

  getExecutionStatus(ref) {
    const taskResult = this.getLastTaskResult(ref);
    if (taskResult) {
      return taskResult.status;
    } else {
      return null;
    }
  }

  switchBranchTaken(caseValue, decisionTaskRef, type) {
    if (!this.taskResultsByRef.has(decisionTaskRef)) return false;

    const switchTaskResult = this.getLastTaskResult(decisionTaskRef);
    const cases = Object.keys(switchTaskResult.workflowTask.decisionCases);

    // Required until DECISION is fully removed
    let resultField =
      type === TaskType.Switch ? "evaluationResult" : "caseOutput";
    const actualValue = _.get(switchTaskResult, `outputData.${resultField}[0]`);
    if (actualValue === undefined) return false;

    if (caseValue) {
      // This is a case branch. Check whether it is the right one.
      return caseValue === actualValue;
    } else {
      // This is the default branch (caseValue === null). Check whether actual value is in one of the other cases
      return !cases.includes(actualValue);
    }
  }

  addVertex(taskConfig, antecedents) {
    const taskResults = taskConfig.aliasForRef
      ? this.taskResultsByRef.get(taskConfig.aliasForRef)
      : this.taskResultsByRef.get(taskConfig.taskReferenceName);
    const lastTaskResult = _.last(taskResults);
    const vertex = {
      taskResults: taskResults || [
        {
          workflowTask: taskConfig,
        },
      ],
      displayName: taskConfig?.displayName ?? taskConfig.name,
      name: taskConfig.name,
      ref: taskConfig.taskReferenceName,
      type: taskConfig.type,
      aliasForRef: taskConfig.aliasForRef,
    };

    if (lastTaskResult) {
      vertex.status = lastTaskResult.status;
    }

    this.graph.setNode(taskConfig.taskReferenceName, vertex);

    for (let antecedent of antecedents) {
      if (antecedent) {
        let antecedentExecuted = !!this.getExecutionStatus(
          antecedent.aliasForRef || antecedent.taskReferenceName
        );
        const edgeParams = {};

        if (
          // taskConfig.type !== TaskType.EndOfDoWhile &&
          taskConfig.name !== TaskType.EndOfDoWhile &&
          (antecedent.type === TaskType.Switch ||
            antecedent.type === "DECISION")
        ) {
          edgeParams.caseValue = getCaseValue(
            taskConfig.taskReferenceName,
            antecedent
          );

          const branchTaken = this.switchBranchTaken(
            edgeParams.caseValue,
            antecedent.taskReferenceName,
            antecedent.type
          );
          if (branchTaken) {
            edgeParams.executed = true;
          }
        } else if (
          lastTaskResult &&
          lastTaskResult.status &&
          antecedentExecuted
        ) {
          edgeParams.executed = true;
        }

        if (
          !(
            // taskConfig.type === TaskType.EndOfDoWhile &&
            (
              taskConfig.name === TaskType.EndOfDoWhile &&
              antecedent.taskReferenceName === taskConfig.taskReferenceName
            )
          )
        ) {
          this.graph.setEdge(
            antecedent.taskReferenceName,
            taskConfig.taskReferenceName,
            edgeParams
          );
        }
      }
    }

    // If task is of type TERMINATE, add an edge to __final
    // if (taskConfig.type === "TERMINATE") {
    //     this.graph.setEdge(taskConfig.taskReferenceName, "__final");
    // }
  }

  processTaskList(tasks, antecedents) {
    let currAntecedents = antecedents;
    if (tasks)
      for (const task of tasks.values()) {
        if (!this.view) {
          currAntecedents = this.processTask(task, currAntecedents);
        }
        if (this.view && this.view === "TICKET_SUMMARY") {
          if (
            task.type === TaskType.ForkJoin ||
            task.type === "FORK_JOIN_DYNAMIC"
          ) {
            const forkJoinIndex = _.findIndex(tasks, function (task) {
              return task.type === TaskType.ForkJoin;
            });
            const joinTask = tasks[forkJoinIndex + 1];
            const hideForkJoin = joinTask ? joinTask.hideTask : false;

            if (!hideForkJoin) {
              currAntecedents = this.processTask(task, currAntecedents);
            }
          } else {
            if (!task.hideTask) {
              currAntecedents = this.processTask(task, currAntecedents);
            }
          }
        }
      }

    return currAntecedents;
  }

  // Nodes are connected to previous
  processSwitchTask(decisionTask, antecedents) {
    console.assert(Array.isArray(antecedents));
    const retval = [];

    this.addVertex(decisionTask, antecedents);

    if (_.isEmpty(decisionTask.defaultCase)) {
      retval.push(decisionTask); // Empty default path
    } else {
      retval.push(
        ...this.processTaskList(decisionTask.defaultCase, [decisionTask])
      );
    }
    if (!_.isEmpty(decisionTask.decisionCases))
      retval.push(
        ..._.flatten(
          Object.entries(decisionTask.decisionCases).map(
            ([caseValue, tasks]) => {
              return this.processTaskList(tasks, [decisionTask]);
            }
          )
        )
      );
    return retval;
  }

  processForkJoinDynamic(dfTask, antecedents) {
    console.assert(Array.isArray(antecedents));

    // This is the DF task (dotted bar) itself.
    this.addVertex(dfTask, antecedents);

    // Only add placeholder if there are 0 spawned tasks for this DF
    const dfTaskResult = this.getLastTaskResult(dfTask.taskReferenceName);
    const forkedTasks = _.get(dfTaskResult, "inputData.forkedTaskDefs");
    const forkedTasksCount = _.get(forkedTasks, "length");

    if (!forkedTasksCount) {
      const placeholderRef = dfTask.taskReferenceName + "_DF_EMPTY_PLACEHOLDER";

      const placeholderTask = {
        name: placeholderRef, // will be overwritten if results available
        taskReferenceName: placeholderRef, // will be overwritten if results available
        type: "DF_EMPTY_PLACEHOLDER",
      };

      if (_.get(dfTaskResult, "status")) {
        // Edge case: Backfill placeholder status for 'no tasks spawned'.
        this.addTaskResult(placeholderRef, { status: dfTaskResult.status });
      }

      this.addVertex(placeholderTask, [dfTask]);
      return [placeholderTask];
    } else {
      return dfTaskResult.inputData.forkedTaskDefs.map((task) => {
        this.addVertex(task, [dfTask]);
        return task;
      });
    }
  }

  getRefTaskChilds(task) {
    switch (task.type) {
      case TaskType.ForkJoin: {
        const outerForkTasks = task.forkTasks || [];
        return _.flatten(
          outerForkTasks.map((innerForkTasks) =>
            innerForkTasks.map((tasks) => tasks)
          )
        );
      }

      case "FORK_JOIN_DYNAMIC": {
        const dfTaskResult = this.getLastTaskResult(task.taskReferenceName);
        const forkedTasks = _.get(dfTaskResult, "inputData.forkedTaskDefs");
        const forkedTasksCount = _.get(forkedTasks, "length");

        if (!forkedTasksCount) {
          const placeholderRef =
            task.taskReferenceName + "_DF_EMPTY_PLACEHOLDER";
          const placeholderTask = {
            name: placeholderRef, // will be overwritten if results available
            taskReferenceName: placeholderRef, // will be overwritten if results available
            type: "DF_EMPTY_PLACEHOLDER",
          };
          return [placeholderTask];
        } else {
          return dfTaskResult.inputData.forkedTaskDefs;
        }
      }

      case "DECISION": // DECISION is deprecated and will be removed in a future release
      case TaskType.Switch: {
        const retval = [];

        // Check if task.decisionCases is null or undefined
        if (task.decisionCases != null) {
          if (!_.isEmpty(task.defaultCase)) {
            retval.push(...this.getRefTask(task.defaultCase));
          }
          retval.push(
            ..._.flatten(
              Object.entries(task.decisionCases).map(([caseValue, tasks]) => {
                return tasks;
              })
            )
          );
        }

        return retval;
      }

      case TaskType.DoWhile: {
        return task.loopOver;
      }

      /*
      case "TERMINATE": 
      case "JOIN": 
      case "TERMINAL":
      case "EVENT":
      case "SUB_WORKFLOW":
      case "EXCLUSIVE_JOIN":
      */
      default: {
        return [];
      }
    }
  }

  getRefTask(task) {
    const taskRefs =
      this.getRefTaskChilds(task)
        ?.map((t) => {
          return this.getRefTask(t);
        })
        .reduce((r, tasks) => {
          return r.concat(tasks);
        }, []) ?? [];
    return [task].concat(taskRefs);
  }

  //modifed old with end of ref name
  processDoWhileTask(doWhileTask, antecedents) {
    console.assert(Array.isArray(antecedents));

    const hasDoWhileExecuted = !!this.getExecutionStatus(
      doWhileTask.taskReferenceName
    );

    this.addVertex(doWhileTask, antecedents);

    var refNameParts = doWhileTask.taskReferenceName.split("_");
    var refId = refNameParts[refNameParts.length - 2];
    var endOfDoWhileRefName = TaskType.EndOfDoWhile + "_" + refId + "_ref";

    // Bottom bar
    // aliasForRef indicates when the bottom bar is clicked one we should highlight the ref
    let endDoWhileTask = {
      type: TaskType.EndOfDoWhile,
      name: doWhileTask.name,
      taskReferenceName: endOfDoWhileRefName,
      aliasForRef: doWhileTask.taskReferenceName,
    };

    const loopOverRefPrefixes = this.getRefTask(doWhileTask).map(
      (t) => t.taskReferenceName
    );
    if (hasDoWhileExecuted) {
      // Create cosmetic LOOP edges between top and bottom bars
      this.graph.setEdge(doWhileTask.taskReferenceName, endOfDoWhileRefName, {
        type: "loop",
        executed: hasDoWhileExecuted,
      });

      const loopOverRefs = Array.from(this.taskResultsByRef.keys()).filter(
        (key) => {
          for (let prefix of loopOverRefPrefixes) {
            if (key.startsWith(prefix + "__")) return true;
          }
          return false;
        }
      );

      const loopTaskResults = [];
      for (let ref of loopOverRefs) {
        const refList = this.taskResultsByRef.get(ref);
        loopTaskResults.push(...refList);
      }

      const loopTasks = loopTaskResults.map((task) => ({
        name: task.taskDefName,
        taskReferenceName: task.referenceTaskName,
        type: task.taskType,
      }));

      for (let task of loopTasks) {
        this.addVertex(task, [doWhileTask]);
      }

      this.addVertex(endDoWhileTask, [...loopTasks]);
    } else {
      // Definition view (or not executed)

      this.processTaskList(doWhileTask.loopOver, [doWhileTask]);

      const lastLoopTask = doWhileTask.loopOver
        ? _.last(doWhileTask.loopOver)
        : [];

      // Connect the end of each case to the loop end
      if (
        lastLoopTask?.type === TaskType.Switch ||
        lastLoopTask?.type === "DECISION"
      ) {
        Object.entries(lastLoopTask.decisionCases).forEach(
          ([caseValue, tasks]) => {
            const lastTaskInCase = _.last(tasks);
            this.addVertex(endDoWhileTask, [lastTaskInCase]);
          }
        );
      }
      if (doWhileTask.loopOver && lastLoopTask) {
        this.addVertex(endDoWhileTask, [lastLoopTask]);
      }
    }

    // Create reverse loop edge
    this.graph.setEdge(doWhileTask.taskReferenceName, endOfDoWhileRefName, {
      type: "loop",
      executed: hasDoWhileExecuted,
    });

    return [endDoWhileTask];
  }

  processForkJoin(forkJoinTask, antecedents) {
    let outerForkTasks = forkJoinTask.forkTasks || [];
    // Add FORK_JOIN task itself (solid bar)
    this.addVertex(forkJoinTask, antecedents);
    // Each sublist is executed in parallel. Tasks within sublist executed sequentially
    return _.flatten(
      outerForkTasks.map((innerForkTasks) =>
        this.processTaskList(innerForkTasks, [forkJoinTask])
      )
    );
  }

  processJoin(joinTask, antecedents) {
    // Process as a normal node UNLESS in special case of an externalized dynamic-fork. In which case - backfill spawned children.

    const taskResult = _.last(
      this.taskResultsByRef.get(joinTask.taskReferenceName)
    );
    const backfilled = [];
    const antecedent = _.first(antecedents);

    if (_.has(taskResult, "inputData.joinOn")) {
      const backfillRefs = taskResult.inputData.joinOn;
      if (_.get(antecedent, "type") === "DF_EMPTY_PLACEHOLDER") {
        const twoBeforeRef = _.first(
          this.graph.predecessors(antecedent.taskReferenceName)
        );
        const twoBefore = this.graph.node(twoBeforeRef);
        if (_.get(twoBefore, "type") === "FORK_JOIN_DYNAMIC") {
          const twoBeforeDef = _.first(twoBefore.taskResults).workflowTask;
          for (let ref of backfillRefs) {
            const tasks = this.taskResultsByRef.get(ref);
            for (let task of tasks) {
              this.addVertex(task.workflowTask, [twoBeforeDef]);
              backfilled.push(task.workflowTask);
            }
          }
        }
      }
    }

    if (backfilled.length > 0) {
      // Remove placeholder if needed
      this.graph.removeNode(antecedent.taskReferenceName);

      // backfilled nodes converge onto join
      this.addVertex(joinTask, backfilled);
    } else {
      this.addVertex(joinTask, antecedents);
    }

    return [joinTask];
  }

  // returns tails = [...]
  processTask(task, antecedents) {
    switch (task.type) {
      case TaskType.ForkJoin: {
        return this.processForkJoin(task, antecedents);
      }

      case "FORK_JOIN_DYNAMIC": {
        return this.processForkJoinDynamic(task, antecedents);
      }

      case "DECISION": // DECISION is deprecated and will be removed in a future release
      case TaskType.Switch: {
        return this.processSwitchTask(task, antecedents);
      }
      case TaskType.DoWhile: {
        return this.processDoWhileTask(task, antecedents);
      }

      case TaskType.EndOfForkJoin: {
        return this.processJoin(task, antecedents);
      }
      default: {
        this.addVertex(task, antecedents);
        return [task];
      }
    }
  }

  getSiblings(taskPointer) {
    let ref;
    if (taskPointer.id) {
      const taskResult = this.taskResultsById.get(taskPointer.id);
      if (taskResult) {
        ref = taskResult.referenceTaskName;
      }
    } else {
      ref = taskPointer.ref;
    }

    if (!ref) return;

    const predecessors = this.graph.predecessors(ref);
    // Nodes might have multiple predecessors e.g. following Decision node.
    // But when parent is FORK_JOIN_DYNAMIC there should only be one.
    if (_.size(predecessors) === 1) {
      const parent = this.graph.node(_.first(predecessors));
      if (parent && parent.status) {
        if (parent.type === "FORK_JOIN_DYNAMIC") {
          return this.graph
            .successors(parent.ref)
            .map((ref) => this.graph.node(ref));
        } else if (parent.type === TaskType.DoWhile) {
          return (
            this.graph
              .successors(parent.ref)
              .map((ref) => this.graph.node(ref))
              // .filter((node) => node.type !== TaskType.EndOfDoWhile);
              .filter((node) => node.type !== TaskType.Label)
          );
          // .filter((node) => node.type !== "DO_WHILE_END");
        }
      }
    }
    // Returns undefined
  }

  findTaskResultById(id) {
    return this.taskResultsById.get(id);
  }

  getRetries(taskPointer) {
    if (taskPointer.id) {
      const taskResult = this.taskResultsById.get(taskPointer.id);
      if (taskResult) {
        const ref = taskResult.referenceTaskName;
        return this.taskResultsByRef.get(ref);
      }
    } else {
      return this.taskResultsByRef.get(taskPointer.ref);
    }
  }

  resolveTaskResult(taskPointer) {
    if (!taskPointer) {
      return null;
    } else if (taskPointer.id) {
      return this.taskResultsById.get(taskPointer.id);
    } else {
      const node = this.graph.node(taskPointer.ref);
      if (!node) {
        return _.last([]);
      }
      return _.last(node.taskResults);
    }
  }
}

function getCaseValue(ref, decisionTask) {
  if (!_.isEmpty(decisionTask.decisionCases))
    for (const [caseValue, taskList] of Object.entries(
      decisionTask.decisionCases
    )) {
      if (!_.isEmpty(taskList) && ref === taskList[0].taskReferenceName) {
        return caseValue;
      }
    }

  return null;
}

/*

Node {
  taskResults: [... TaskResult]
}

TaskResult {
  ...[Task Result fields only present if executed],
  workflowTask: {
    ... Always populated
  }
}

*/
